// Library Methods
import { useEffect, useRef } from "react";

// Components
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import { ChevronDownIcon } from "src/assets/icons/ChevronDownIcon";

// Styles
import {
  CustomTextFieldProps,
  getSurfaceTokens,
  getRequiredErrorColor,
} from "src/styles/mui/muiStylesHelper";

type NumberInputFieldType = {
  id?: string;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder: string;
  value: string;
  min: number;
  max: number;
  height?: string;
  onChange: (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  onBlur?: (value: string) => void;
};

const NumberInputField = ({
  id,
  error = false,
  helperText = undefined,
  required = false,
  disabled = false,
  placeholder,
  value,
  min,
  max,
  height = undefined,
  onChange,
  onBlur = undefined,
}: NumberInputFieldType) => {
  const theme = useTheme();
  const { onSurface, inputBorder } = getSurfaceTokens(theme);
  const inputRef = useRef<HTMLInputElement>(null);
  const stepCommitTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Clear any pending debounced commit on unmount, so it can't fire after
  // this instance (and whatever it's bound to) is gone.
  useEffect(() => {
    return () => clearTimeout(stepCommitTimeoutRef.current);
  }, []);

  const handleStep = (direction: number) => {
    const currentValue = parseFloat(value) || 0;
    const newValue = Math.min(max, Math.max(min, currentValue + direction));

    onChange({
      target: { value: String(newValue) },
    } as React.ChangeEvent<HTMLInputElement>);

    // Debounced so spam-clicking doesn't fire a save after every click --
    // only once the clicking has actually stopped for a moment.
    clearTimeout(stepCommitTimeoutRef.current);
    stepCommitTimeoutRef.current = setTimeout(() => {
      onBlur?.(String(newValue));
    }, 500);
  };

  // Allows only digits, "-", ".", and copy/paste/undo-style shortcuts
  const blockInvalidChars = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
      "Enter",
      "Escape",
    ];

    if (event.ctrlKey || event.metaKey || allowedKeys.includes(event.key)) {
      return;
    }

    if (!/^[0-9.-]$/.test(event.key)) {
      event.preventDefault();
    }
  };

  // Update the input value to something valid on blurring the input
  const handleBlur = () => {
    // A real blur supersedes any pending debounced commit from the step
    // buttons -- this already covers committing the current value.
    clearTimeout(stepCommitTimeoutRef.current);

    const isValidNumber = value !== "" && !Number.isNaN(Number(value));
    let finalValue = value;

    // handle invalid number
    if (!isValidNumber) {
      finalValue = "";
    }
    // handle min-max ranges
    else {
      const numericValue = Number(value);
      if (numericValue > max) finalValue = String(max);
      else if (numericValue < min) finalValue = String(min);
    }

    // Check the DOM input value to see if it needs updating
    if (inputRef.current && inputRef.current.value !== finalValue) {
      inputRef.current.value = finalValue;
    }

    if (finalValue !== value) {
      onChange({
        target: { value: finalValue },
      } as React.ChangeEvent<HTMLInputElement>);
    }

    onBlur?.(finalValue);
  };

  return (
    <TextField
      id={id ?? "number-input-field"}
      variant="outlined"
      color="warning"
      type="text" // use type="text" so that value always matches the onscreen input
      inputMode="decimal"
      required={required}
      disabled={disabled}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onBlur={handleBlur}
      onKeyDown={blockInvalidChars}
      inputRef={inputRef}
      fullWidth
      sx={{
        "& .MuiFormHelperText-root.Mui-error": {
          color: getRequiredErrorColor(theme),
          marginTop: "4px",
          marginLeft: 0,
        },
      }}
      InputProps={{
        inputProps: {
          // min/max no longer included here because the input is type="text"
          sx: {
            fontSize: "14px",
            "&::placeholder": {
              color: inputBorder,
              opacity: 1,
            },
          },
        },
        endAdornment: (
          <InputAdornment position="end">
            <Box
              className="number-spinner"
              sx={{ display: "flex", flexDirection: "column", mr: "-6px" }}
            >
              <IconButton
                size="small"
                tabIndex={-1}
                disabled={disabled}
                onClick={() => handleStep(1)}
                sx={{ padding: "1px", color: onSurface, borderRadius: "2px" }}
              >
                <ChevronDownIcon
                  style={{ transform: "rotate(180deg)", width: 12, height: 12 }}
                />
              </IconButton>
              <IconButton
                size="small"
                tabIndex={-1}
                disabled={disabled}
                onClick={() => handleStep(-1)}
                sx={{ padding: "1px", color: onSurface, borderRadius: "2px" }}
              >
                <ChevronDownIcon style={{ width: 12, height: 12 }} />
              </IconButton>
            </Box>
          </InputAdornment>
        ),
        sx: {
          ...CustomTextFieldProps(theme),
          ...(height && { height }),
          ".MuiInputBase-input": { padding: "0px 8px 0px 16px" },
          ".MuiOutlinedInput-notchedOutline": {
            border: `1px solid ${inputBorder}`,
          },
          "& .number-spinner": { visibility: "hidden" },
          "&:hover .number-spinner, &:focus-within .number-spinner": {
            visibility: "visible",
          },
        },
      }}
      error={error}
      helperText={helperText}
    />
  );
};

export default NumberInputField;
